<template>
  <div>
  <base-drawer
    class="drawer-form gift-card-purchase-drawer"
    :show="show"
    title="New gift card purchase"
    width="640px"
    @close="onClose"
  >
    <drawer-section title="Gift card" icon="icon-gift-2">
      <el-carousel v-if="designs.length > 0" height="220px" v-on:change="setGiftCardDesign" :autoplay="false" indicator-position="outside">
        <el-carousel-item v-for="(item, index) in designs" :key="'CS' + index">
          <img style="border: 3px solid white; object-fit: contain; width: 100%; height: 100%" :src="item.url" />
        </el-carousel-item>
      </el-carousel>

      <base-input label="Gift card code">
        <segmented-toggle
          size="sm"
          :value="gift_card.custom_code"
          :options="codeModeOptions"
          @input="gift_card.custom_code = $event"
        />
      </base-input>
      <div class="form-field" v-if="gift_card.custom_code == 1">
        <base-input :error="errs.custom_code" label="Custom code" placeholder="Custom code" v-model="gift_card.code"></base-input>
      </div>

      <div class="form-row">
        <div class="form-field form-field--half">
          <base-input label="Gift card type">
            <el-select
              v-model="selectedGiftCardType"
              class="select-primary"
              value-key="id"
              v-on:change="selectGiftCardType()"
            >
              <el-option
                v-for="(gct, index) in giftCardTypes"
                class="select-primary"
                :label="gct.name"
                :value="{id: gct.id, name: gct.name, include_taxes_and_fees: gct.include_taxes_and_fees,
                  value_type: gct.value_type, designs: gct.designs, price: gct.price,
                  redemption_amount: gct.redemption_amount, linked_game_ids: gct.linked_game_ids,
                  game_display_mode: gct.game_display_mode}"
                :key="'AMOUNT' + index"
              >
                <span>{{gct.name}}</span>
              </el-option>
            </el-select>
          </base-input>
        </div>
        <div class="form-field form-field--half" v-if="selectedGiftCardType.value_type === 0">
          <base-input label="Custom value">
            <money-input v-model="gift_card.custom_amount" @change="calculateTotals()" />
          </base-input>
        </div>
        <div class="form-field form-field--half" v-if="selectedGiftCardType.value_type === 2 && selectedGiftCardType.game_display_mode !== 1">
          <base-input label="Event">
            <el-select
              v-model="selectedGame"
              class="select-primary"
              value-key="id"
              v-on:change="calculateGameValue()"
            >
              <el-option
                v-for="(game, index) in getFilteredGames()"
                class="select-primary"
                :label="game.name"
                :value="{id: game.id,
              name: game.name,
              cumulative_category_pricing: game.cumulative_category_pricing,
              game_pricing: game.game_pricing,
              pricing_type_id: game.pricing_type_id,
              pricing_categories: game.pricing_categories}"
                :key="'GAME' + index"
              >
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="form-field" v-if="selectedGiftCardType.value_type === 2" :key="gc_key">
        <div class="pricing-row" v-for="(c, i) in selectedGame.pricing_categories" :key="'C' + i">
          <div class="pricing-row__info">
            <span class="pricing-row__name text-white">{{ c.category }}</span>
          </div>
          <div class="pricing-row__qty">
            <base-input class="mb-0">
              <el-input-number @change="calculateGameValue()" v-model="c.number_of_players" :min="0" size="mini" />
            </base-input>
          </div>
        </div>
      </div>

      <base-input label="Quantity" class="mt-3">
        <div class="pricing-row__qty">
          <el-input-number v-on:change="calculateTotals()" v-model="gift_card.quantity" placeholder="1" :min="1" size="mini"></el-input-number>
        </div>
      </base-input>

      <div class="summary-line">
        <span class="text-white">Value</span>
        <span class="text-white">{{ getAmount(gift_card.unit_value) }}</span>
      </div>
      <div class="summary-line" v-if="selectedGiftCardType.value_type === 1">
        <span class="text-white">Price</span>
        <span class="text-white">{{ getAmount(gift_card.unit_price) }}</span>
      </div>
      <div class="summary-line summary-line--total">
        <span class="text-white">Total cost</span>
        <span class="text-white">{{ getAmount(gift_card.total) }}</span>
      </div>
    </drawer-section>

    <drawer-section title="Delivery" icon="icon-send">
      <div class="form-field">
        <segmented-toggle
          size="sm"
          :value="gift_card.send_instantly"
          :options="deliveryModeOptions"
          @input="onDeliveryModeChange"
        />
      </div>
      <div class="form-field" v-if="gift_card.send_instantly == 0">
        <base-input label="Delivery date">
          <el-date-picker :editable="false"
            class="custom-cursor"
            type="date"
            placeholder="Select date"
            value-format="yyyy-MM-dd"
            :format="day_of_week + '' + date_format_cal"
            v-model="gift_card.delivery_date"
            :picker-options="{ disabledDate: (time) => disabledDates(time, gift_card.delivery_date), firstDayOfWeek: first_day_of_week }"
          >
          </el-date-picker>
        </base-input>
      </div>
    </drawer-section>

    <drawer-section title="Recipient" icon="icon-single-02">
      <div class="toggle-row form-field">
        <el-switch active-color="#e14eca" v-model="send_gift_card_to_self" />
        <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="send_gift_card_to_self = !send_gift_card_to_self">
          Purchasing customer is the recipient
        </label>
      </div>
      <template v-if="!send_gift_card_to_self">
        <div class="form-row">
          <div class="form-field form-field--half">
            <base-input :error="errs.recipient_first_name" label="First name" v-model="gift_card.recipient_first_name" placeholder="Recipient's first name"></base-input>
          </div>
          <div class="form-field form-field--half">
            <base-input :error="errs.recipient_last_name" label="Last name" v-model="gift_card.recipient_last_name" placeholder="Recipient's last name"></base-input>
          </div>
        </div>
        <div class="form-field">
          <base-input :error="errs.recipient_email" label="Email" v-model="gift_card.recipient_email" placeholder="Recipient's email"></base-input>
        </div>
      </template>
      <base-textarea
        label="Personal message"
        :rows="2"
        :maxlength="10000"
        :show-word-limit="false"
        placeholder="Add your personal message..."
        v-model="gift_card.personal_message"
      />
    </drawer-section>

    <drawer-section title="Pricing" icon="icon-coins">
      <div class="summary-line">
        <span class="text-white">Subtotal</span>
        <span class="text-white">{{ getAmount(subtotal) }}</span>
      </div>

      <div class="summary-line" v-for="(f, i) in fees" :key="'F' + i" v-show="!waive_fee && subtotal > 0">
        <span class="text-white">{{ f.name }}</span>
        <span class="text-white">{{ getAmount(getFee(f)) }}</span>
      </div>

      <div class="summary-line" v-for="(p, i) in promos" :key="pc_key + i" v-show="subtotal > 0">
        <span class="text-white">
          {{ p.name }}
          <button class="remove-promo" type="button" @click="removePromoCode(p)">
            <i class="tim-icons icon-simple-remove"></i>
          </button>
        </span>
        <span class="text-danger">-{{ getAmount(getDiscount(p)) }}</span>
      </div>

      <div class="summary-line summary-line--total">
        <span class="text-white">Total</span>
        <span class="text-white">{{ getAmount(total) }}</span>
      </div>

      <div class="waive-row">
        <el-switch v-model="waive_fee" @change="calculateTotals()" active-color="#e14eca" />
        <span class="ml-2 text-white">Waive fees</span>
      </div>

      <div class="form-field mt-3" v-if="!override_price && selectedGiftCardType.value_type !== 2">
        <base-button variant="outlined" tone="primary" size="sm" v-on:click="override_price = true">
          <i class="tim-icons icon-pencil"></i> Override price
        </base-button>
      </div>
      <div class="form-field mt-3" v-if="override_price">
        <div class="form-row">
          <div class="form-field--narrow">
            <base-input label="Price">
              <money-input v-model="new_price" />
            </base-input>
          </div>
        </div>
        <div class="mt-2">
          <base-button type="secondary" size="sm" v-on:click="cancelOverridePrice()">Cancel</base-button>
          <base-button type="primary" class="ml-2" size="sm" v-on:click="updatePrice()">Override</base-button>
        </div>
      </div>
    </drawer-section>

    <drawer-section title="Promo code" icon="icon-tag">
      <base-input class="mb-0">
        <el-autocomplete
          style="width: 100%"
          v-model="promo_code"
          :fetch-suggestions="promoSearch"
          placeholder="Search promo..."
          :trigger-on-focus="false"
          @select="handlePromoSelect"
        >
          <template #default="{ item }">
            <div>
              <span class="text-primary">{{ item.name }}</span>
              <span v-if="!hasBuyXGetYRule(item.rules)"> - </span>
              <small>
                <template v-if="!hasBuyXGetYRule(item.rules)">
                  <span v-if="item.redemption_type === 0">{{ item.value_amount }}%</span>
                  <span v-if="item.redemption_type === 1">{{ getAmount(item.value_amount) }}</span>
                  <span v-if="item.value_type === 0"> per transaction</span>
                  <span v-if="item.value_type === 1"> per line item</span>
                  <span v-if="item.value_type === 2"> per quantity</span>
                </template>
                <span
                  class="ml-2"
                  style="text-transform: uppercase; line-height: 9px; color: white; background-color: #1d8cf8; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                  v-if="hasBuyXGetYRule(item.rules)"
                >buy x get y</span>
                <span
                  class="ml-2"
                  style="text-transform: uppercase; line-height: 9px; color: white; background-color: #344675; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                  v-if="item.internal_only"
                >internal only</span>
                <span
                  class="ml-2"
                  style="text-transform: uppercase; line-height: 9px; color: white; background-color: #00bf9a; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                  v-if="hasMinValueRule(item.rules)"
                >value rule</span>
                <span
                  class="ml-2"
                  style="text-transform: uppercase; line-height: 9px; color: white; background-color: #ff8d72; border-radius: 2px; padding: 2px 5px; font-size: 9px"
                  v-if="hasBundlePriceRule(item.rules)"
                >bundle price</span>
              </small>
            </div>
          </template>
          <template #append>
            <button class="apply-promo-append" type="button" @click="applyPromoCode">Apply</button>
          </template>
        </el-autocomplete>
      </base-input>
    </drawer-section>

    <drawer-section title="Customer details" icon="icon-single-02">
      <base-input label="Customer lookup">
        <el-autocomplete
          style="width: 100%"
          v-model="lookup_value"
          :fetch-suggestions="querySearch"
          placeholder="Search by name, email, or phone"
          :trigger-on-focus="false"
          @select="handleSelect"
        >
          <template #default="{ item }">
            <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
            <small class="font-italic">
              <i class="tim-icons icon-email-85"></i> {{ item.email }}
              <span v-if="item.phone">
                <i class="tim-icons el-icon-mobile-phone ml-2"></i> {{ item.phone }}
              </span>
            </small>
          </template>
          <template #append>
            <button class="lookup-clear-append" type="button" @click="clearCustomer">
              <i class="tim-icons icon-refresh-02"></i>
            </button>
          </template>
        </el-autocomplete>
      </base-input>

      <div class="form-row">
        <div class="form-field form-field--half">
          <base-input :error="errs.first_name" label="First name" v-model="user.first_name" placeholder="First name"></base-input>
        </div>
        <div class="form-field form-field--half">
          <base-input :error="errs.last_name" label="Last name" v-model="user.last_name" placeholder="Last name"></base-input>
        </div>
      </div>
      <div class="form-row">
        <div class="form-field form-field--half">
          <base-input :error="errs.email" label="Email" v-model="user.email" placeholder="Email"></base-input>
        </div>
        <div class="form-field form-field--half">
          <base-input type="text" label="Phone (optional)">
            <phone-number-input ref="phoneInput"
                                v-model="user.phone" @phone-update="handlePhoneUpdate"
                                :default-country="countryCode || 'US'"
            />
          </base-input>
        </div>
      </div>
    </drawer-section>

    <drawer-section title="Payment options" icon="icon-credit-card">
      <div class="form-row">
        <div class="form-field form-field--half">
          <base-input label="Payment method" :error="errs.payment_option">
            <div class="form-row" v-if="posDevice">
              <div class="form-field--half">
                <el-input class="select-primary" :value="'Send to POS: ' + posDevice.name" readonly></el-input>
              </div>
              <button type="button" class="row-remove" aria-label="Clear POS device" @click="posDevice = null">
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </div>
            <div class="form-row" v-else>
              <div class="form-field--half">
                <el-select
                  class="select-primary"
                  v-model="selectedPaymentOption"
                  placeholder="Select payment method"
                  value-key="id"
                >
                  <el-option
                    class="select-primary"
                    v-for="(item, index) in paymentOptions"
                    :key="index + 'P'"
                    :label="item.name"
                    :value="{id: item.id, name: item.name, content: item.content, is_default: item.is_default}"
                  >
                  </el-option>
                </el-select>
              </div>
              <span class="form-row__or">or</span>
              <send-to-pos-button
                variant="field"
                pick-only
                :disabled="loading"
                @pick="onPosDevicePicked"
              />
            </div>
          </base-input>
        </div>
        <!-- Stripe stored cards dropdown -->
        <div class="form-field form-field--half" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 3 && settings.store_credit_card && stripe_payment_methods.length > 0">
          <base-input label="Stored cards">
            <el-select
              class="select-primary"
              v-model="selected_payment_method"
              placeholder="Select card"
              @change="onPaymentMethodChange"
            >
              <el-option class="select-primary" label="Use a new card" value="new"></el-option>
              <el-option
                class="select-primary"
                v-for="pm in stripe_payment_methods"
                :key="'gc_stripe_' + pm.id"
                :label="getCardBrandName(pm.card_brand) + ' ****' + pm.card_last_four + (pm.is_default ? ' (Default)' : '')"
                :value="pm.id"
              >
              </el-option>
            </el-select>
          </base-input>
        </div>
        <!-- Square stored cards dropdown -->
        <div class="form-field form-field--half" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 2 && settings.square_store_credit_card && square_payment_methods.length > 0">
          <base-input label="Stored cards">
            <el-select
              class="select-primary"
              v-model="selected_payment_method"
              placeholder="Select card"
              @change="onPaymentMethodChange"
            >
              <el-option class="select-primary" label="Use a new card" value="new"></el-option>
              <el-option
                class="select-primary"
                v-for="pm in square_payment_methods"
                :key="'gc_square_' + pm.id"
                :label="getCardBrandName(pm.card_brand) + ' ****' + pm.card_last_four + (pm.is_default ? ' (Default)' : '')"
                :value="pm.id"
              >
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="form-field" v-if="!selectedPaymentOption.is_default">
        <div v-html="selectedPaymentOption.content"></div>
      </div>

      <div class="form-row" v-show="selectedPaymentOption.name === 'Customer credit'">
        <div class="form-field form-field--half">
          <base-input :label="'Available (' + currency_symbol + ')'" v-model="available_credit" disabled></base-input>
        </div>
        <div class="form-field form-field--half">
          <base-input :label="'Amount to apply (' + currency_symbol + ')'" v-model="applied_credit"></base-input>
        </div>
      </div>

      <div class="form-field" v-show="selectedPaymentOption.name === 'Gift card' && selectedGiftCardCode">
        <base-input :label="'Available balance (' + currency_symbol + ')'" v-model="selectedGiftCardCode.remaining" disabled></base-input>
      </div>

      <div class="form-field" v-show="selectedPaymentOption.name === 'Gift card'">
        <base-input label="Gift card code" :error="errs.gift_card_code">
          <el-autocomplete
            style="width: 100%"
            v-model="gift_card_code"
            :fetch-suggestions="giftCardSearch"
            placeholder="Search by code..."
            :trigger-on-focus="false"
            @select="handleGiftCardSelect"
          >
            <template #default="{ item }">
              <p style="margin-bottom: -12px" class="text-primary">{{ getGiftCardCode(item.code) }}</p>
              <small class="font-italic">
                <i class="tim-icons icon-money-coins"></i> Total {{ getAmount(item.total) }} Remaining {{ getAmount(item.remaining) }}
              </small>
            </template>
          </el-autocomplete>
        </base-input>
      </div>

      <!-- Authorize.net (Accept.js) -->
      <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 1">
        <enhanced-payment-form
          ref="authorizePaymentForm"
          :api-login-id="settings.api_name"
          :public-client-key="settings.public_client_key"
          :is-live="!!settings.is_live"
          :show-security-indicators="false"
        />
      </div>

      <!-- Square stored card display -->
      <div class="stored-card-display" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 2 && selected_payment_method !== 'new' && selectedStoredCard">
        <div class="d-flex align-items-center">
          <i :class="getCardBrandIcon(selectedStoredCard.card_brand)" class="stored-card-icon mr-3"></i>
          <div>
            <p class="mb-0 text-white">{{ getCardBrandName(selectedStoredCard.card_brand) }} ****{{ selectedStoredCard.card_last_four }}</p>
            <small style="color: rgba(255,255,255,0.5);">Expires {{ selectedStoredCard.card_exp_month }}/{{ selectedStoredCard.card_exp_year }}</small>
          </div>
        </div>
      </div>

      <!-- Square new card form -->
      <div class="form-field" v-show="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 2 && (selected_payment_method === 'new' || square_payment_methods.length === 0)">
        <form id="payment-form-square-gift-card">
          <div id="card-container-gift-card"></div>
        </form>
        <div id="payment-status-container-gift-card"></div>
      </div>

      <!-- Square save card toggle -->
      <div class="toggle-row form-field" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 2 && settings.square_store_credit_card && (selected_payment_method === 'new' || square_payment_methods.length === 0) && user.end_user_id">
        <el-switch active-color="#e14eca" v-model="save_card_for_future"></el-switch>
        <label class="mb-0 ml-2 text-white">Save this card for future payments</label>
      </div>

      <!-- Stripe stored card display -->
      <div class="stored-card-display" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 3 && selected_payment_method !== 'new' && selectedStoredCard">
        <div class="d-flex align-items-center">
          <i :class="getCardBrandIcon(selectedStoredCard.card_brand)" class="stored-card-icon mr-3"></i>
          <div>
            <p class="mb-0 text-white">{{ getCardBrandName(selectedStoredCard.card_brand) }} ****{{ selectedStoredCard.card_last_four }}</p>
            <small style="color: rgba(255,255,255,0.5);">Expires {{ selectedStoredCard.card_exp_month }}/{{ selectedStoredCard.card_exp_year }}</small>
          </div>
        </div>
      </div>

      <!-- Stripe new card form -->
      <div class="form-field" v-show="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 3 && (selected_payment_method === 'new' || stripe_payment_methods.length === 0)">
        <form id="payment-form-gift-card">
          <label for="card-element-gift-card">Credit or debit card</label>
          <div id="card-element-gift-card"></div>
          <div id="card-errors-gift-card" role="alert"></div>
        </form>
      </div>

      <!-- Stripe save card toggle -->
      <div class="toggle-row form-field" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 3 && settings.store_credit_card && (selected_payment_method === 'new' || stripe_payment_methods.length === 0) && user.end_user_id">
        <el-switch active-color="#e14eca" v-model="save_card_for_future"></el-switch>
        <label class="mb-0 ml-2 text-white">Save this card for future payments</label>
      </div>

      <!-- Paystack info -->
      <div class="form-field" v-if="selectedPaymentOption.name === 'Card' && settings.payment_gateway_id === 4">
        <div class="d-flex align-items-center" style="padding: 8px 0;">
          <i class="tim-icons icon-lock-circle mr-2 text-success"></i>
          <span class="text-white small">You will be redirected to Paystack to complete the payment securely.</span>
        </div>
      </div>
    </drawer-section>

    <drawer-section title="Email options" icon="icon-email-85">
      <div class="toggle-row form-field">
        <el-switch active-color="#e14eca" v-model="no_confirmation_email" />
        <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="no_confirmation_email = !no_confirmation_email">
          Do not send purchase confirmation email to the customer
        </label>
      </div>
      <div class="toggle-row">
        <el-switch active-color="#e14eca" v-model="suppress_delivery_email" />
        <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="suppress_delivery_email = !suppress_delivery_email">
          Do not send gift card delivery email to the recipient
        </label>
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" :disabled="loading" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="loading" @click="createGiftPurchase()">Create</base-button>
      </div>
    </template>
  </base-drawer>

  <base-alert-dialog
    :show="alertDialog.show"
    :title="alertDialog.title"
    :message="alertDialog.message"
    :variant="alertDialog.variant"
    @confirm="alertDialog.show = false"
    @cancel="alertDialog.show = false"
  />
  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import currencies from "@/assets/currencies.json";
import {API_LOCATION} from "@/api-config";
import {diacriticIncludes} from "@/util/search";
import axios from "axios";
import {CarouselItem, Carousel, Autocomplete} from "element-ui";
import _ from "lodash";
import moment from "moment"
import {formatPhoneDisplay} from "@/util/phoneFormatter";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import EnhancedPaymentForm from "@/components/Payments/EnhancedPaymentForm";
import SendToPosButton from '@/components/POS/SendToPosButton'
import posPaymentService from '@/services/posPayment'
import { roundCents, calculateEventGiftCardValue, calculateLineDiscounts, calculateFeeBreakdown, calculateBuyXGetYDiscount, calculateBundlePriceDiscount } from '@/util/pricing'

// Console gift card purchase form. Owns the purchase payload, customer lookup,
// and every payment gateway branch (Authorize.net,
// Square, Stripe, Paystack). The list page passes the reference data it
// already loads (types, events, fees, settings) and refreshes on `created`.
export default {
  name: "GiftCardPurchaseDrawer",
  components: {
    [CarouselItem.name]: CarouselItem,
    [Carousel.name]: Carousel,
    [Autocomplete.name]: Autocomplete,
    SendToPosButton,
    PhoneNumberInput,
    EnhancedPaymentForm
  },
  props: {
    show: { type: Boolean, default: false },
    // Active gift card types (linked_game_ids parsed, game_display_mode defaulted).
    giftCardTypes: { type: Array, default: () => [] },
    // Events with game_pricing parsed and pricing_categories built.
    games: { type: Array, default: () => [] },
    fees: { type: Array, default: () => [] },
    // Settings row from the gift purchases list (payment_gateway_id, api_name, api_location, is_live).
    paymentSettings: { type: [Object, String], default: '' }
  },
  data() {
    const group = JSON.parse(localStorage.getItem('group'))
    return {
      // OTC-3111: device chosen with the Send to POS button. Create then
      // creates the order pending and dispatches it to this device.
      posDevice: null,
      countryCode: JSON.parse(localStorage.getItem('company')).country,
      first_day_of_week: group.first_day_of_week || 7,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: group.id,
      currencies: currencies.currencies,
      number_format: group.number_format,
      currency_symbol: '$',
      currency: group.currency,
      currency_format: group.currency_format,
      date_format: group.date_format,
      date_format_cal: '',
      day_of_week: '',
      loading: false,
      alertDialog: { show: false, title: '', message: '', variant: 'info' },
      send_gift_card_to_self: false,
      game_taxes: [],
      game_fees: [],
      new_price: 0,
      number_of_players: 0,
      override_price: false,
      // Staff price override per card (null = list price). Kept here and
      // re-applied on every recompute so a quantity change never drops it.
      override_unit_price: null,
      // Per fee id and per promo id amounts from the last recompute, for the rows.
      fee_amounts: {},
      promo_discounts: {},
      total_discount: 0,
      squareAppId: '',
      squareLocationId: '',
      selectedGiftCardCode: '',
      customer_credits: 0,
      available_credit: 0,
      applied_credit: 0,
      gift_card_code: '',
      payment: {
        cardNumber: '',
        expirationDate: '',
        cardCode: '',
        zip: ''
      },
      authorize_opaque_data: null,
      paymentOptions: [],
      selectedPaymentOption: {name: '', content: '', id: '', is_default: ''},
      gc_key: 0,
      gift_card: {
        type: '',
        custom_code: 0,
        code: '',
        personal_message: '',
        design_url: '',
        custom_amount: '',
        recipient_first_name: '',
        recipient_last_name: '',
        recipient_email: '',
        total: {amount: 0},
        quantity: 1,
        send_instantly: 1,
        delivery_date: new Date(),
        gift_card_type_id: '',
        unit_value: 0,
        unit_price: 0
      },
      settings: '',
      designs: [],
      no_confirmation_email: false,
      suppress_delivery_email: false,
      selectedGame: '',
      selectedGiftCardType: '',
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        phoneE164: ''
      },
      waive_fee: false,
      subtotal: 0,
      total: 0,
      total_fee: 0,
      lookup_value: '',
      promo_code: '',
      selectedPromoCode: '',
      promos: [],
      pc_key: 0,
      errs: {
        payment_option: '',
        first_name: '',
        last_name: '',
        email: '',
        payment_amount: '',
        partial_amount: '',
        recipient_first_name: '',
        recipient_last_name: '',
        recipient_email: '',
        custom_code: '',
        gift_card_code: ''
      },
      // Stripe stored payment methods
      stripe_customer: null,
      stripe_payment_methods: [],
      // Square stored payment methods
      square_customer: null,
      square_payment_methods: [],
      // Shared for both gateways
      selected_payment_method: 'new',
      save_card_for_future: false,
      // Gateway SDK handles
      stripe: null,
      elements: null,
      card: null,
      square: null
    }
  },
  computed: {
    selectedStoredCard() {
      if (this.selected_payment_method === 'new' || !this.selected_payment_method) {
        return null
      }
      if (this.settings.payment_gateway_id === 3) {
        return this.stripe_payment_methods.find(pm => pm.id === this.selected_payment_method)
      } else if (this.settings.payment_gateway_id === 2) {
        return this.square_payment_methods.find(pm => pm.id === this.selected_payment_method)
      }
      return null
    },
    codeModeOptions() {
      return [
        { value: 0, label: 'Generate code' },
        { value: 1, label: 'Custom code' }
      ]
    },
    deliveryModeOptions() {
      return [
        { value: 1, label: 'Send instantly' },
        { value: 0, label: 'Send on a future date' }
      ]
    }
  },
  watch: {
    show(visible) {
      if (visible) {
        document.addEventListener('keydown', this.onEscKey)
        this.initForm()
      } else {
        document.removeEventListener('keydown', this.onEscKey)
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onEscKey)
  },
  mounted() {
    this.date_format_cal = this.date_format
    this.date_format_cal = this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    this.currency_symbol = _.find(this.currencies, { code: this.currency }).symbol
  },
  methods: {
    showAlert(title, message, variant = 'info'){
      this.alertDialog = { show: true, title, message, variant }
    },
    onClose() {
      if (this.loading) return
      this.$emit('close')
    },
    onEscKey(e) {
      if (e.key === 'Escape') this.onClose()
    },
    onDeliveryModeChange(value) {
      this.gift_card.send_instantly = value
      if (Number(value) === 1) {
        this.setDeliveryDate()
      }
    },
    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    handlePhoneUpdate(phoneData) {
      this.user.phoneE164 = phoneData.e164;
    },
    selectGiftCardType(){
      this.designs = this.selectedGiftCardType ? JSON.parse(this.selectedGiftCardType.designs) : []
      this.gift_card.design_url = this.designs.length > 0 ? this.designs[0] : ''


      // A new type starts from its own list price: drop any override and the
      // previous type's unit price (the stale price used to be billed per card).
      this.override_unit_price = null
      this.override_price = false
      this.new_price = 0
      this.gift_card.unit_price = 0
      if(this.selectedGiftCardType.value_type === 0 || this.selectedGiftCardType.value_type === 2){
        this.gift_card.custom_amount = ''
        this.gift_card.unit_value = 0
        this.gift_card.total = 0
        this.subtotal = 0
        this.total = 0
      }
      // Promos were fetched for the previous type; the server only applies a
      // promo to a card whose type it is linked to (or a cross-group promo).
      this.promos = this.promos.filter(p => this.promoCoversType(p, this.selectedGiftCardType.id))

      if(this.selectedGiftCardType.value_type === 0 || this.selectedGiftCardType.value_type === 1){
        this.calculateTotals()
      }
      if(this.selectedGiftCardType.value_type === 2){
        let filteredGames = this.getFilteredGames()
        if(this.selectedGiftCardType.game_display_mode === 1 && filteredGames.length > 0){
          this.selectedGame = {
            id: filteredGames[0].id,
            name: filteredGames[0].name,
            cumulative_category_pricing: filteredGames[0].cumulative_category_pricing,
            game_pricing: filteredGames[0].game_pricing,
            pricing_type_id: filteredGames[0].pricing_type_id,
            pricing_categories: filteredGames[0].pricing_categories
          }
        } else if(filteredGames.length > 0){
          this.selectedGame = {
            id: filteredGames[0].id,
            name: filteredGames[0].name,
            cumulative_category_pricing: filteredGames[0].cumulative_category_pricing,
            game_pricing: filteredGames[0].game_pricing,
            pricing_type_id: filteredGames[0].pricing_type_id,
            pricing_categories: filteredGames[0].pricing_categories
          }
        } else {
          this.selectedGame = this.games[0]
        }
        this.calculateGameValue()
      }
      if(!this.selectedGiftCardType){
        this.gift_card.total = 0
      }

      this.gc_key++
    },
    async giftCardSearch(queryString, cb){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/gifts/autocomplete?q=' + encodeURIComponent(queryString)
      axios.get(url, config)
        .then(response => {
          let suggestions = []
          for(let n in response.data.gift_cards){
            if(response.data.gift_cards[n].code != null){
              suggestions.push({
                value: JSON.stringify(response.data.gift_cards[n]),
                id: response.data.gift_cards[n].id,
                code: response.data.gift_cards[n].code,
                customer_id: response.data.gift_cards[n].customer_id,
                date_purchased: response.data.gift_cards[n].date_purchased,
                email: response.data.gift_cards[n].email,
                first_name: response.data.gift_cards[n].first_name,
                last_name: response.data.gift_cards[n].last_name,
                remaining: response.data.gift_cards[n].remaining,
                total: response.data.gift_cards[n].total
              })
            }

          }
          const results = suggestions

          cb(results)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    handleGiftCardSelect(item){
      this.gift_card_code = this.getGiftCardCode(item.code)
      this.selectedGiftCardCode = item
    },
    calculateGameValue(){
      this.gift_card.unit_value = 0
      this.number_of_players = 0
      if(this.selectedGame.cumulative_category_pricing){
        if(this.selectedGame.pricing_type_id === 1) {
          for (let m in this.selectedGame.pricing_categories) {
            this.number_of_players += this.selectedGame.pricing_categories[m].number_of_players
          }
          for (let m in this.selectedGame.pricing_categories) {
            for(let n in this.selectedGame.game_pricing){
              if(this.selectedGame.game_pricing[n].category_id === this.selectedGame.pricing_categories[m].category_id
                && this.number_of_players >= this.selectedGame.game_pricing[n].min_players
                && this.number_of_players <= this.selectedGame.game_pricing[n].max_players) {
                this.gift_card.unit_value += this.selectedGame.game_pricing[n].price * this.selectedGame.pricing_categories[m].number_of_players
                break
              }
            }
            let last_index = _.findLastKey(this.selectedGame.game_pricing, {category_id: this.selectedGame.pricing_categories[m].category_id})
            if(this.number_of_players > this.selectedGame.game_pricing[last_index].max_players){
              this.gift_card.unit_value += this.selectedGame.game_pricing[last_index].price * this.selectedGame.pricing_categories[m].number_of_players
            }
            let smallest_price_category = _.find(this.selectedGame.game_pricing, {category_id : this.selectedGame.pricing_categories[m].category_id})
            if(this.number_of_players !== 0 &&
              this.number_of_players < smallest_price_category.min_players){
              this.gift_card.unit_value += smallest_price_category.price * this.selectedGame.pricing_categories[m].number_of_players
            }
          }
        }
        else if(this.selectedGame.pricing_type_id === 2){
          for (let m in this.selectedGame.pricing_categories) {
            this.number_of_players += this.selectedGame.pricing_categories[m].number_of_players
          }
          for(let m in this.selectedGame.pricing_categories){
            // Empty categories are never sold; total headcount only picks the tier (OTC-2911)
            if(!(this.selectedGame.pricing_categories[m].number_of_players > 0)) continue
            for(let n in this.selectedGame.game_pricing){
              if(this.selectedGame.game_pricing[n].category_id === this.selectedGame.pricing_categories[m].category_id
                && this.number_of_players >= this.selectedGame.game_pricing[n].min_players
                && this.number_of_players <= this.selectedGame.game_pricing[n].max_players) {
                this.gift_card.unit_value += this.selectedGame.game_pricing[n].price
                break
              }
            }
            let last_index = _.findLastKey(this.selectedGame.game_pricing, {category_id: this.selectedGame.pricing_categories[m].category_id})
            if(this.number_of_players > this.selectedGame.game_pricing[last_index].max_players){
              this.gift_card.unit_value += this.selectedGame.game_pricing[last_index].price
            }

            let smallest_price_category = _.find(this.selectedGame.game_pricing, {category_id : this.selectedGame.pricing_categories[m].category_id})
            if(this.number_of_players !== 0 &&
              this.number_of_players < smallest_price_category.min_players){
              this.gift_card.unit_value += smallest_price_category.price
            }
          }
        }
      } else {
        if(this.selectedGame.pricing_type_id === 1){
          for(let m in this.selectedGame.pricing_categories){

            this.number_of_players += this.selectedGame.pricing_categories[m].number_of_players
            for(let n in this.selectedGame.game_pricing){
              if(this.selectedGame.game_pricing[n].category_id === this.selectedGame.pricing_categories[m].category_id
                && this.selectedGame.pricing_categories[m].number_of_players >= this.selectedGame.game_pricing[n].min_players
                && this.selectedGame.pricing_categories[m].number_of_players <= this.selectedGame.game_pricing[n].max_players) {
                this.gift_card.unit_value += this.selectedGame.game_pricing[n].price * this.selectedGame.pricing_categories[m].number_of_players
                break
              }
            }
            let last_index = _.findLastKey(this.selectedGame.game_pricing, {category_id: this.selectedGame.pricing_categories[m].category_id})
            if(this.selectedGame.pricing_categories[m].number_of_players > this.selectedGame.game_pricing[last_index].max_players){
              this.gift_card.unit_value += this.selectedGame.game_pricing[last_index].price * this.selectedGame.pricing_categories[m].number_of_players
            }

            let smallest_price_category = _.find(this.selectedGame.game_pricing, {category_id : this.selectedGame.pricing_categories[m].category_id})
            if(this.selectedGame.pricing_categories[m].number_of_players !== 0 &&
              this.selectedGame.pricing_categories[m].number_of_players < smallest_price_category.min_players){
              this.gift_card.unit_value += smallest_price_category.price * this.selectedGame.pricing_categories[m].number_of_players
            }
          }
        }
        else if(this.selectedGame.pricing_type_id === 2){
          for(let m in this.selectedGame.pricing_categories){
            this.number_of_players += this.selectedGame.pricing_categories[m].number_of_players
            for(let n in this.selectedGame.game_pricing){
              if(this.selectedGame.game_pricing[n].category_id === this.selectedGame.pricing_categories[m].category_id
                && this.selectedGame.pricing_categories[m].number_of_players >= this.selectedGame.game_pricing[n].min_players
                && this.selectedGame.pricing_categories[m].number_of_players <= this.selectedGame.game_pricing[n].max_players) {
                this.gift_card.unit_value += this.selectedGame.game_pricing[n].price
                break
              }
            }
            let last_index = _.findLastKey(this.selectedGame.game_pricing, {category_id: this.selectedGame.pricing_categories[m].category_id})
            if(this.selectedGame.pricing_categories[m].number_of_players > this.selectedGame.game_pricing[last_index].max_players){
              this.gift_card.unit_value += this.selectedGame.game_pricing[last_index].price
            }

            let smallest_price_category = _.find(this.selectedGame.game_pricing, {category_id : this.selectedGame.pricing_categories[m].category_id})
            if(this.selectedGame.pricing_categories[m].number_of_players !== 0 &&
              this.selectedGame.pricing_categories[m].number_of_players < smallest_price_category.min_players){
              this.gift_card.unit_value += smallest_price_category.price
            }
          }
        }
      }

      if(this.selectedGiftCardType.include_taxes_and_fees){
        this.gift_card.unit_value = calculateEventGiftCardValue({
          subtotal: this.gift_card.unit_value,
          taxes: this.game_taxes,
          fees: this.game_fees,
          numberOfPlayers: this.number_of_players,
          gameId: this.selectedGame ? this.selectedGame.id : null
        })
      }

      this.gift_card.total = this.gift_card.unit_value * this.gift_card.quantity

      this.calculateTotals()

    },
    removePromoCode(item){
      _.remove(this.promos, {
        id: item.id
      });
      this.pc_key++
      this.calculateTotals()
    },
    // Amount the last recompute attributed to this promo (shown on its row).
    getDiscount(promo){
      return this.promo_discounts[promo.id] || 0
    },
    promoCoversType(promo, giftCardTypeId){
      if(promo.is_cross_group) return true
      if(promo.gift_card_types === undefined || promo.gift_card_types === null) return true
      const types = typeof promo.gift_card_types === 'string' ? JSON.parse(promo.gift_card_types) : promo.gift_card_types
      return Array.isArray(types) && !!_.find(types, {id: Number(giftCardTypeId)})
    },
    applyPromoCode(){
      if(this.selectedPromoCode.hasOwnProperty('id')){
        let dupe = 0
        for(let n in this.promos){
          if(this.selectedPromoCode.id === this.promos[n].id){
            dupe++
          }

          if(this.promos[n].is_combineable === 0 || this.selectedPromoCode.is_combineable === 0){
            this.showAlert('Error', 'You cannot apply multiple non-combineable promo codes', 'danger')
            return
          }
        }
        if(dupe === 0){
          // The promo is sent as-is; the pricing helpers clamp a flat amount to
          // the subtotal the same way the server does.
          this.promos.push(this.selectedPromoCode)
        }
      }
      this.promo_code = ''

      this.calculateTotals()
    },
    getTotalDiscount(){
      return this.total_discount
    },
    async promoSearch(queryString, cb){
      let purchase_date = new Date()
      purchase_date = purchase_date.getFullYear() +  "-" +  ('0' + (purchase_date.getMonth() + 1)).slice(-2) + "-" + ('0' + purchase_date.getDate()).slice(-2);

      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/promos?autocomplete=' + encodeURIComponent(queryString) + `&purchase_date=` + purchase_date + `&applicable_to_gift_cards=1`
        + `&gift_card_type=` + this.selectedGiftCardType.id

      axios.get(url, config)
        .then(response => {
          let suggestions = []

          for(let n in response.data.promos){
            if(response.data.promos[n].name != null){
              suggestions.push({
                value: JSON.stringify(response.data.promos[n]),
                id: response.data.promos[n].id,
                name: response.data.promos[n].name,
                description: response.data.promos[n].description,
                value_amount: response.data.promos[n].value_amount,
                redemption_type: response.data.promos[n].redemption_type,
                value_type: response.data.promos[n].value_type,
                internal_only: response.data.promos[n].internal_only,
                is_combineable: response.data.promos[n].is_combineable,
                is_cross_group: response.data.promos[n].is_cross_group,
                gift_card_types: response.data.promos[n].gift_card_types,
                rules: response.data.promos[n].rules || []
              })
            }

          }
          const results = queryString
            ? suggestions.filter(this.createFilter(queryString))
            : suggestions;

          cb(results)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    handlePromoSelect(item){
      this.promo_code = item.name
      this.selectedPromoCode = item
    },
    hasBuyXGetYRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'BUY_X_GET_Y');
    },
    hasMinValueRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'MIN_VALUE');
    },
    hasBundlePriceRule(rules) {
      if (!rules || rules.length === 0) return false;
      return rules.some(r => r.rule_type?.rule_key === 'BUNDLE_PRICE');
    },
    async initializeStripe(){
      // A Stripe Connect venue tokenizes inside its connected account (platform key + account).
      const accountOpts = this.settings.stripe_account_id ? { stripeAccount: this.settings.stripe_account_id } : undefined;
      this.stripe = window.Stripe(this.settings.api_name, accountOpts);
      this.elements = this.stripe.elements();

      const style = {
        base: {
          fontSize: '16px',
          fontFamily: 'Poppins, sans-serif',
          color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff'
        },
      };

      // Create an instance of the card Element.
      this.card = this.elements.create('card', {style});
      // Add an instance of the card Element into the `card-element` <div>.
      this.card.mount('#card-element-gift-card');
    },
    async initializeSquare(){
      if (!window.Square) {
        throw new Error('Square.js failed to load properly');
      }
      if(this.card){
        await this.card.destroy()
      }
      this.square = window.Square.payments(this.squareAppId, this.squareLocationId);
      try {
        this.card = await this.initializeCard(this.square);
      } catch (e) {
        console.error('Initializing Card failed', e);
        return;
      }
    },
    async initializeCard(payments) {
      const customStyle = {
        '.input-container.is-focus': {
          borderColor: '#e14eca',
        },
        '.input-container.is-error': {
          borderColor: '#ff1600',
        },
        '.message-text.is-error': {
          color: '#ff1600',
        },
        '.message-icon.is-error': {
          color: '#ff1600',
        },
        'input.is-error': {
          color: '#ff1600',
        },
        '.message-icon': {
          color: '#adb5bd',
        },
        '@media screen and (max-width: 600px)': {
          'input': {
            'fontSize': '12px',
          }
        },
        '.input-container': {
          borderRadius: '6px',
        },
        '.message-text': {
          color: '#adb5bd',
        },
        input: {
          backgroundColor: localStorage.getItem('mode') === '0' ? '#ffffff' : '#22233a',
          color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff',
        },
        'input::placeholder': {
          color: '#adb5bd',
        },
      }
      const card = await payments.card({
        style: customStyle,
      });
      await card.attach('#card-container-gift-card');
      return card;
    },
    // Stripe stored payment methods
    async loadStripePaymentMethods() {
      if (!this.user.end_user_id || this.settings.payment_gateway_id !== 3) {
        return
      }
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/customers/' + this.user.end_user_id + '/stripe-customer'

        const response = await this.axios.get(url, config)
        if (response.data.stripe_customer) {
          this.stripe_customer = response.data.stripe_customer
          this.stripe_payment_methods = response.data.payment_methods || []

          // Auto-select default card or first card
          if (this.stripe_payment_methods.length > 0) {
            const defaultCard = this.stripe_payment_methods.find(pm => pm.is_default)
            this.selected_payment_method = defaultCard ? defaultCard.id : this.stripe_payment_methods[0].id
          } else {
            this.selected_payment_method = 'new'
          }
        }
      } catch (err) {
        console.log('No Stripe customer found or error fetching:', err)
        this.stripe_customer = null
        this.stripe_payment_methods = []
        this.selected_payment_method = 'new'
      }
    },
    resetStripePaymentMethods() {
      this.selected_payment_method = 'new'
      this.save_card_for_future = false
      this.stripe_customer = null
      this.stripe_payment_methods = []
    },
    // Square stored payment methods
    async loadSquarePaymentMethods() {
      if (!this.user.end_user_id || this.settings.payment_gateway_id !== 2) {
        return
      }
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/customers/' + this.user.end_user_id + '/square-customer'

        const response = await this.axios.get(url, config)
        if (response.data.square_customer) {
          this.square_customer = response.data.square_customer
          this.square_payment_methods = response.data.payment_methods || []

          // Auto-select default card or first card
          if (this.square_payment_methods.length > 0) {
            const defaultCard = this.square_payment_methods.find(pm => pm.is_default)
            this.selected_payment_method = defaultCard ? defaultCard.id : this.square_payment_methods[0].id
          } else {
            this.selected_payment_method = 'new'
            await this.reinitializeSquareForm()
          }
        }
      } catch (err) {
        console.log('No Square customer found or error fetching:', err)
        this.square_customer = null
        this.square_payment_methods = []
        this.selected_payment_method = 'new'
        await this.reinitializeSquareForm()
      }
    },
    resetSquarePaymentMethods() {
      this.selected_payment_method = 'new'
      this.save_card_for_future = false
      this.square_customer = null
      this.square_payment_methods = []
    },
    async reinitializeSquareForm() {
      if (this.settings.payment_gateway_id === 2) {
        if (this.card) {
          try {
            await this.card.destroy()
          } catch (e) {
            console.log('Error destroying card:', e)
          }
          this.card = null
        }
        await this.$nextTick()
        try {
          await this.initializeSquare()
        } catch (e) {
          console.log('Error reinitializing Square form:', e)
        }
      }
    },
    async onPaymentMethodChange() {
      if (this.selected_payment_method === 'new') {
        // Force fresh payment form mount when switching to new card
        if (this.settings.payment_gateway_id === 3) {
          // Stripe
          if (this.card) {
            this.card.unmount()
          }
          await this.$nextTick()
          if (this.card) {
            this.card.mount('#card-element-gift-card')
          }
        } else if (this.settings.payment_gateway_id === 2) {
          // Square
          await this.reinitializeSquareForm()
        }
      }
      this.save_card_for_future = false
    },
    getCardBrandName(brand) {
      const names = {
        'visa': 'Visa',
        'mastercard': 'Mastercard',
        'amex': 'American Express',
        'american_express': 'American Express',
        'discover': 'Discover',
        'diners': 'Diners Club',
        'diners_club': 'Diners Club',
        'jcb': 'JCB',
        'unionpay': 'UnionPay',
        'unknown': 'Card'
      }
      return names[brand?.toLowerCase()] || brand || 'Card'
    },
    getCardBrandIcon(brand) {
      const icons = {
        'visa': 'fab fa-cc-visa',
        'mastercard': 'fab fa-cc-mastercard',
        'amex': 'fab fa-cc-amex',
        'american_express': 'fab fa-cc-amex',
        'discover': 'fab fa-cc-discover',
        'diners': 'fab fa-cc-diners-club',
        'diners_club': 'fab fa-cc-diners-club',
        'jcb': 'fab fa-cc-jcb',
      }
      return icons[brand?.toLowerCase()] || 'fas fa-credit-card'
    },
    setDeliveryDate(){
      this.gift_card.delivery_date = new Date()
    },
    clearCustomer(){
      this.lookup_value = ''
      this.user = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        phoneE164: '',
        end_user_id: null
      }
      // Reset stored payment methods
      this.resetStripePaymentMethods()
      this.resetSquarePaymentMethods()
    },
    async querySearch(queryString, cb) {
      //make API call to /customers with queryString as filter
      var token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/customers?count=10000000&offset=0&first_name=&last_name=&email=&phone=&game=&tag=&autocomplete=' + encodeURIComponent(queryString)
      axios.get(url, config)
        .then(response => {
          let suggestions = []
          for(let n in response.data.customers){
            if(response.data.customers[n].email != null
              && response.data.customers[n].first_name != null
              && response.data.customers[n].first_name != ''){
              suggestions.push({
                value: JSON.stringify(response.data.customers[n]),
                email: response.data.customers[n].email,
                id: response.data.customers[n].id,
                end_user_id: response.data.customers[n].end_user_id,
                first_name: response.data.customers[n].first_name,
                last_name: response.data.customers[n].last_name,
                phone: this.formatPhone(response.data.customers[n].country, response.data.customers[n].phone)
              })
            }

          }
          const results = suggestions;

          cb(results)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    createFilter(queryString) {
      return (suggestion) => {
        return diacriticIncludes(suggestion.value, queryString);
      };
    },
    async handleSelect(item){
      this.lookup_value = item.email
      this.user.first_name = item.first_name
      this.user.last_name = item.last_name
      this.user.email = item.email
      this.user.phone = item.phone
      this.user.end_user_id = item.end_user_id
      this.getCredits(item.end_user_id)

      // Load stored payment methods for the selected customer
      if (this.settings.payment_gateway_id === 2 && this.settings.square_store_credit_card) {
        await this.loadSquarePaymentMethods()
      } else if (this.settings.payment_gateway_id === 3 && this.settings.store_credit_card) {
        await this.loadStripePaymentMethods()
      }
    },
    setGiftCardDesign(index){
      this.gift_card.design_url = this.designs[index]
    },
    disabledDates(time) {
      let date = new Date();
      let previousDate = date.setDate(date.getDate() - 1);
      return time.getTime() < previousDate;
    },
    // Amount the last recompute attributed to this fee (shown on its row).
    getFee(f){
      return this.fee_amounts[f.id] || 0
    },
    // Cancel returns the card to its list price.
    cancelOverridePrice(){
      this.override_unit_price = null
      this.override_price = false
      this.new_price = 0
      this.calculateTotals()
    },
    updatePrice(){
      const price = parseFloat(this.new_price)
      if(!Number.isFinite(price) || price < 0){
        this.showAlert('Error', 'Enter a valid price', 'danger')
        return
      }
      this.override_unit_price = roundCents(price)
      this.override_price = false
      this.calculateTotals()
    },
    // MIN_VALUE rule against this order, as update-transaction-values gates it
    // (isMinValueEligible): the cards' prices in cents against min and max.
    isMinValueMet(config, subtotal){
      if(!config) return true
      const cents = (v) => Math.round(Number((v != null ? v : 0) + 'e2'))
      const total = cents(subtotal)
      const min = config.min_amount != null ? cents(config.min_amount) : null
      const max = config.max_amount != null ? cents(config.max_amount) : null
      if(min != null && total < min) return false
      if(max != null && total > max) return false
      return true
    },
    // Mandatory fees only, as update-transaction-values loads them
    // (is_active = 1 AND optional = 0). The list page's lambda already filters
    // this way; the guard keeps any other caller honest.
    chargeableFees(){
      return (this.fees || []).filter(f => Number(f.is_active) !== 0 && !f.optional)
    },
    // The one place money is computed. Rebuilds the per-card lines from the
    // current state and runs them through the shared pricing engine, so the
    // payload matches what update-transaction-values will bill:
    //   subtotal  = per-card list price × quantity
    //   discount  = value promos per card (rounded per card) + rule promos, clamped to the subtotal
    //   fee       = mandatory fees against the per-card lines, in order, with the backend's headroom
    //   total     = subtotal + fee - discount
    calculateTotals(){
      const type = this.selectedGiftCardType
      if(!type){
        this.subtotal = 0
        this.total = 0
        this.total_fee = 0
        this.total_discount = 0
        this.fee_amounts = {}
        this.promo_discounts = {}
        return
      }
      const quantity = Math.max(1, parseInt(this.gift_card.quantity) || 1)

      // Per-card value (what the card is worth) and list price (what is billed).
      if(type.value_type === 0){
        // A blank or invalid amount prices at 0 but the field is left as typed.
        const custom = parseFloat(this.gift_card.custom_amount)
        const amount = Number.isFinite(custom) && custom > 0 ? roundCents(custom) : 0
        this.gift_card.unit_value = amount
        this.gift_card.unit_price = this.override_unit_price !== null ? this.override_unit_price : amount
      } else if(type.value_type === 1){
        this.gift_card.unit_value = roundCents(Number(type.redemption_amount) || 0)
        this.gift_card.unit_price = this.override_unit_price !== null ? this.override_unit_price : roundCents(Number(type.price) || 0)
      } else if(type.value_type === 2){
        // Event value from calculateGameValue (taxes and fees folded in when the
        // type says so). The create lambda bills this value per card, so there
        // is no override on event cards.
        this.gift_card.unit_value = roundCents(Number(this.gift_card.unit_value) || 0)
        this.gift_card.unit_price = this.gift_card.unit_value
      }
      this.gift_card.total = roundCents(this.gift_card.unit_value * quantity)

      // One line per card: the backend reads one gift_cards row per card.
      const unitPrice = this.gift_card.unit_price
      const lines = []
      for(let i = 0; i < quantity; i++){
        lines.push({ lineId: 'gc' + i, kind: 'gift_card', giftCardTypeId: type.id, gameId: null, itemId: null, amount: unitPrice, quantity: 1 })
      }
      this.subtotal = roundCents(unitPrice * quantity)

      // Promos, one at a time in the order applied, following the backend's
      // calculateDiscounts per promo: a MIN_VALUE rule that is not met drops the
      // promo entirely; a buy X get Y promo with no value amount is rule-only;
      // a bundle price promo is always rule-only; otherwise the value promo
      // math runs, rounded per card. The rule evaluators see one entry per
      // card (the backend reads one gift_cards row per card), keyed like the
      // lines so their per-card shares feed the per-card fees below.
      const giftCardsForRules = lines.map(l => ({ id: l.lineId, gift_card_type_id: type.id, total: unitPrice, quantity: 1 }))
      const promoDiscounts = {}
      let discount = 0
      const perLine = new Map()
      const addPerLine = (map) => {
        for(const [lineId, amounts] of map){
          const cur = perLine.get(lineId) || 0
          perLine.set(lineId, roundCents(cur + (amounts.shelf || 0)))
        }
      }
      for(const promo of this.promos){
        const rules = Array.isArray(promo.rules) ? promo.rules : []
        const ruleKey = (r) => r && r.rule_type && r.rule_type.rule_key
        const minValueRules = rules.filter(r => ruleKey(r) === 'MIN_VALUE')
        const hasBxgy = rules.some(r => ruleKey(r) === 'BUY_X_GET_Y')
        const hasBundle = rules.some(r => ruleKey(r) === 'BUNDLE_PRICE')
        let amount = 0
        if(minValueRules.length > 0 && !minValueRules.every(r => this.isMinValueMet(r.rule_config, this.subtotal))){
          amount = 0
        } else {
          let ruleOnly = false
          if(hasBxgy){
            const bxgy = calculateBuyXGetYDiscount({ promos: [promo], cart: [], items: [], giftCards: giftCardsForRules, taxes: [] })
            amount = roundCents(amount + bxgy.shelfDiscount)
            addPerLine(bxgy.perLine)
            ruleOnly = Number(promo.value_amount) === 0
          }
          if(!ruleOnly && hasBundle){
            const bundle = calculateBundlePriceDiscount({ promos: [promo], cart: [], items: [], giftCards: giftCardsForRules, taxes: [] })
            amount = roundCents(amount + bundle.shelfDiscount)
            addPerLine(bundle.perLine)
            ruleOnly = true
          }
          if(!ruleOnly){
            const value = calculateLineDiscounts({ promos: [promo], lines, taxes: [] })
            amount = roundCents(amount + value.shelfDiscount)
            addPerLine(value.perLine)
          }
        }
        promoDiscounts[promo.id] = amount
        promo.applied_discount = amount
        discount = roundCents(discount + amount)
      }
      // The server caps the whole order's discount at its subtotal.
      if(discount > this.subtotal){
        discount = this.subtotal
      }
      this.promo_discounts = promoDiscounts
      this.total_discount = discount

      // Fees. A percent-per-card fee that is covered by promo codes uses the
      // card's own discount share (value and rule promos alike).
      const feeLines = lines.map(l => ({ ...l, discount: perLine.get(l.lineId) || 0 }))
      const feeResult = calculateFeeBreakdown(this.chargeableFees(), this.subtotal, 0, this.waive_fee, { discount, lines: feeLines })
      const feeAmounts = {}
      for(const row of feeResult.feeAmounts){
        feeAmounts[row.id] = row.amount
      }
      this.fee_amounts = feeAmounts
      this.total_fee = feeResult.totalFee

      this.total = roundCents(this.subtotal + this.total_fee - discount)
      if(this.total < 0){
        this.total = 0
      }
    },
    async tokenize(paymentMethod) {
      const tokenResult = await paymentMethod.tokenize();

      if (tokenResult.status === 'OK') {
        const zip = tokenResult.details.billing.postalCode ? tokenResult.details.billing.postalCode : ''
        return {token: tokenResult.token, zip}
      } else {
        let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
        if (tokenResult.errors) {
          errorMessage += ` and errors: ${JSON.stringify(
            tokenResult.errors
          )}`;
        }

        throw new Error(errorMessage);
      }
    },
    async verifyBuyer(payments, token, zip) {
      const verificationDetails = {
        amount: this.subtotal.toString(),
        /* collected from the buyer */
        billingContact: {
          familyName: this.user.last_name,
          givenName: this.user.first_name,
          postalCode: zip
        },
        currencyCode: this.currency,
        intent: 'CHARGE',
      };

      const verificationResults = await payments.verifyBuyer(
        token,
        verificationDetails
      );
      return verificationResults.token;
    },
    validateGiftCardForm({ skipPaymentOption = false } = {}){
      let errors = 0

      if(skipPaymentOption){
        this.errs.payment_option = ''
      } else if(this.selectedPaymentOption.name === ''){
        this.errs.payment_option = 'Please select a payment option'
        errors++
      } else if(this.total <= 0 && this.selectedPaymentOption.name === 'Card' && this.settings.payment_gateway_id !== 3){
        this.errs.payment_option = "Invalid charge amount for payment option"
        errors++
      } else if(this.total <= 0.5 && this.selectedPaymentOption.name === 'Card' && this.settings.payment_gateway_id === 3){
        this.errs.payment_option = "Invalid charge amount for payment option"
        errors++
      } else {
        this.errs.payment_option = ""
      }

      if(this.gift_card.custom_code == 1 && this.gift_card.code === ''){
        this.errs.custom_code = 'Gift card code cannot be empty'
        errors++
      } else if(this.gift_card.custom_code == 1 && this.gift_card.quantity > 1){
        this.errs.custom_code = 'You are trying to create more than one gift card with the same custom code. Code must be unique.'
        errors++
      } else if(this.gift_card.custom_code == 1 && this.gift_card.code.indexOf(' ') >= 0){
        this.errs.custom_code = 'Gift card code cannot have spaces'
        errors++
      }
      else {
        this.errs.custom_code = ''
      }

      if(this.gift_card.recipient_first_name === ''){
        this.errs.recipient_first_name = 'Recipient first name cannot be empty'
        errors++
      } else {
        this.errs.recipient_first_name = ''
      }

      if(this.gift_card.recipient_last_name === ''){
        this.errs.recipient_last_name = 'Recipient last name cannot be empty'
        errors++
      } else {
        this.errs.recipient_last_name = ''
      }

      if(this.gift_card.recipient_email === ''){
        this.errs.recipient_email = 'Recipient email cannot be empty'
        errors++
      } else {
        this.errs.recipient_email = ''
      }

      if(this.user.first_name === ''){
        this.errs.first_name = 'First name cannot be empty'
        errors++
      } else {
        this.errs.first_name = ''
      }

      if(this.user.last_name === ''){
        this.errs.last_name = 'Last name cannot be empty'
        errors++
      } else {
        this.errs.last_name = ''
      }

      if(this.user.email === ''){
        this.errs.email = 'Email cannot be empty'
        errors++
      } else {
        this.errs.email = ''
      }

      return errors > 0 ? false : true

    },
    async getCredits(id){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/customers/' + id + '/customer-credit'
      axios.get(url, config)
        .then(response => {
          this.customer_credits = response.data.customer_credits
          this.getAvailableCredit()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    getAvailableCredit(){
      let available = 0
      for(let n in this.customer_credits){
        available = available + parseFloat(this.customer_credits[n].remaining)
      }
      this.available_credit = available.toFixed(2)
      if(parseFloat(this.available_credit) < parseFloat(this.gift_card.total.amount)){
        this.applied_credit = this.available_credit
      } else {
        this.applied_credit = this.gift_card.total.amount
      }
    },
    async getPaymentMethods(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/payment-methods'

      // Awaited so initForm's selectGiftCardType runs with taxes, fees and
      // payment settings loaded; an event card with include_taxes_and_fees
      // on was otherwise valued before they arrived.
      try {
        let response = await axios.get(url, config)
        this.paymentOptions = response.data.payment_methods
        this.game_taxes = response.data.taxes
        this.game_fees = response.data.fees
        this.selectedPaymentOption = this.paymentOptions.length > 0 ? this.paymentOptions[0] : {}

        // Capture payment settings from the response
        if (response.data.payment_settings) {
          this.settings = response.data.payment_settings
          // Cast boolean settings
          if (this.settings.store_credit_card !== undefined) {
            this.settings.store_credit_card = Boolean(this.settings.store_credit_card)
          }
          if (this.settings.create_customer !== undefined) {
            this.settings.create_customer = Boolean(this.settings.create_customer)
          }
          if (this.settings.square_store_credit_card !== undefined) {
            this.settings.square_store_credit_card = Boolean(this.settings.square_store_credit_card)
          }
          if (this.settings.square_create_customer !== undefined) {
            this.settings.square_create_customer = Boolean(this.settings.square_create_customer)
          }
        }
      } catch (err) {
        this.loading = false
        console.error(err)
        if(err.response && err.response.status == 401){
          this.$router.push('/forbidden')
        } else {
          this.showAlert('Error', 'Something went wrong', 'danger')
        }
      }
    },
    async payWithPaystack() {
      let amount = Math.round(Number(parseFloat(this.total) + 'e2'))

      // Metadata lets the paystack-webhook orphan-log this charge with full
      // recovery context if the callback below never reaches the lambda (the
      // cart exists only in this browser). A session hiccup must never block
      // a charge, so the operator email is best-effort.
      let operatorEmail = null
      try {
        const session = await Auth.currentSession()
        operatorEmail = session.getIdToken().payload.email
      } catch (e) {
        operatorEmail = null
      }
      const popup = new PaystackPop()
      popup.newTransaction({
        key: this.settings.api_name,
        email: this.user.email,
        amount: amount,
        reference: ''+Math.floor((Math.random() * 1000000000) + 1),
        metadata: {
          otc: 1,
          otc_flow: 'console_new_transaction',
          otc_company_group_id: JSON.parse(localStorage.getItem('group') || '{}').id,
          otc_company_id: localStorage.getItem('companyId'),
          ...(operatorEmail ? { otc_user_email: operatorEmail } : {})
        },
        onSuccess: (response) => {
          // The lambda reads paystack_reference.transaction (v1 field name);
          // v2 carries the numeric transaction id as response.trans (verified from a live callback).
          this.handlePaystackPayment({ ...response, transaction: response.trans })
        }
      });
    },
    async handlePaystackPayment(reference){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId +  '/groups/' + companyGroupId +'/transactions'

      let data = {
        bookings: [],
        gift_card: this.gift_card,
        subtotal: this.subtotal,
        total: this.total,
        waive_taxes: 0,
        waive_fees: this.waive_fee ? 1 : 0,
        waive_fee: this.waive_fee,
        tax: 0,
        discount: this.getTotalDiscount(),
        no_confirmation_email: this.no_confirmation_email,
        suppress_delivery_email: this.suppress_delivery_email,
        send_payment_request: this.send_payment_request,
        promo_codes: this.promos,
        fee: this.total_fee,
        user: {
          ...this.user,
          phone: this.user.phoneE164 || this.user.phone  // Use E.164 if available
        },
        paystack_reference: reference,
        payment: {cardNumber: '1111'},
      }

      if(this.pay_partial_amount){
        if(parseFloat(this.partial_amount) > parseFloat(this.total)){
          this.errs.partial_amount = 'Amount cannot be greater than total'
          this.pa_key++
          return
        } else {
          this.errs.partial_amount = ''
          this.pa_key++
        }
        data.partial_amount = this.partial_amount
      }
      data.user.phone = data.user.phone === null ? '' : data.user.phone

      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Gift card has been created', 'success')
          this.loading = false
          this.$emit('created')
          this.$emit('close')
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else if(err.response.status == 500){
            this.showAlert('Error', err.response.data, 'danger')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    applySelfRecipient(){
      if(this.send_gift_card_to_self){
        this.gift_card.recipient_first_name = this.user.first_name
        this.gift_card.recipient_last_name = this.user.last_name
        this.gift_card.recipient_email = this.user.email
      }
    },
    // Shared by the payment method path and the Send to POS path. Gateway
    // specific keys (card tokens, credit, applied gift card) are added by
    // createGiftPurchase only.
    buildPurchasePayload(payment_type){
      this.gift_card.price = this.gift_card.unit_price
      this.gift_card.total = this.gift_card.unit_value
      this.gift_card.gift_card_type_id = this.selectedGiftCardType.id
      this.gift_card.delivery_date = moment(this.gift_card.delivery_date, 'YYYY-MM-DD').format('YYYY-MM-DD')
      return {
        subtotal: this.subtotal,
        tax: 0,
        fee: this.total_fee,
        total: this.total,
        user: {
          ...this.user,
          phone: this.user.phoneE164 || this.user.phone  // Use E.164 if available
        },
        waive_taxes: 0,
        waive_fees: this.waive_fee ? 1 : 0,
        waive_fee: this.waive_fee,
        payment_type: payment_type,
        custom_payment_type: !this.selectedPaymentOption.is_default,
        no_confirmation_email: this.no_confirmation_email,
        suppress_delivery_email: this.suppress_delivery_email,
        promo_codes: this.promos,
        gift_card: this.gift_card,
        discount: this.getTotalDiscount(),
        bookings: []
      }
    },
    async putTransaction(data){
      let tok = await this.getAuthToken()
      let config = {headers: {Authorization: tok}}
      let url = API_LOCATION + 'companies/'+ this.companyId + '/groups/' + this.companyGroupId + '/transactions'
      return this.axios.put(url, data, config)
    },
    handleCreateError(err){
      console.log(err)
      if(err.response && err.response.status == 401){
        this.$router.push('/forbidden')
      } else if(err.response && err.response.status == 500){
        this.showAlert('Error', err.response.data, 'danger')
      } else {
        this.showAlert('Error', 'Something went wrong', 'danger')
      }
    },
    onPosDevicePicked(device){
      this.posDevice = device
      // The device replaces the payment method, so drop any method picked
      // earlier: its inputs (card form, credit, gift card code) hide with it.
      this.selectedPaymentOption = {name: '', content: '', id: '', is_default: ''}
      this.errs.payment_option = ''
    },
    // OTC-3111: the order is created pending (nothing paid, no emails) and
    // dispatched to the chosen device; pos-complete-session finalizes it when
    // the customer taps.
    async createAndSendToPos(){
      if(!(Number(this.total) > 0)){
        this.$notify({ type: 'warning', message: 'There is nothing to collect on this gift card. Use a payment method instead.' })
        return
      }
      // Same floor as the Card method on Stripe (validateGiftCardForm): the
      // device charges through the same gateway.
      if(this.settings.payment_gateway_id === 3 && Number(this.total) <= 0.5){
        this.$notify({ type: 'warning', message: 'The amount is too small to collect on a POS device.' })
        return
      }
      // Strict single-session policy, same as SendToPosButton: one in-flight
      // POS payment at a time so a customer is never prompted on two surfaces.
      const existing = posPaymentService.state.activeSession
      if (existing && !['paid', 'failed', 'cancelled', 'expired'].includes(existing.status)) {
        this.$notify({ type: 'warning', message: 'A payment is already in progress on ' + existing.deviceName + '. Cancel it before starting another.' })
        posPaymentService.expand()
        return
      }
      const device = this.posDevice
      const data = this.buildPurchasePayload('pos_dispatch')
      data.custom_payment_type = false
      data.partial_amount = 0
      this.loading = true
      let transactionId
      try {
        const response = await this.putTransaction(data)
        transactionId = Number(response.data && response.data.transaction_id)
        if(!transactionId){
          throw new Error('create returned no transaction id')
        }
      } catch (err) {
        this.loading = false
        this.handleCreateError(err)
        return
      }
      // When pos-send-order cannot deliver it cancels the pending order itself
      // (pending_order_cancelled in its error body), so the form stays open for
      // another try. Any other failure (network) may leave the order pending;
      // it is still collectable from transaction details by its order number.
      try {
        await posPaymentService.sendToDevice({ transactionId, device, amount: Number(this.total) || null })
        this.$notify({ type: 'success', message: 'Gift card created. Sent to ' + device.name + '.' })
      } catch (err) {
        console.error('Send to POS after gift card create failed', err)
        const body = err.response && err.response.data
        const cancelled = !!(body && typeof body === 'object' && body.pending_order_cancelled)
        this.loading = false
        if(cancelled){
          this.$notify({ type: 'danger', message: 'Could not send to ' + device.name + '. The gift card was not created. Try again.' })
        } else {
          this.$notify({ type: 'danger', message: 'Could not send to ' + device.name + '. The order was saved as pending; open it from Transactions to send it again or cancel it.' })
        }
        return
      }
      this.finishCreate()
    },
    async createGiftPurchase(){
      this.applySelfRecipient()

      if(!this.validateGiftCardForm({ skipPaymentOption: !!this.posDevice })){
        return
      }
      if(this.posDevice){
        await this.createAndSendToPos()
        return
      }

      let payment_type = 'pos'
      if(this.selectedPaymentOption.name === 'Cash'){
        payment_type = 'cash'
      } else if(this.selectedPaymentOption.name === 'Card'){
        payment_type = 'card'
      }
      if(!this.selectedPaymentOption.is_default){
        payment_type = this.selectedPaymentOption.name
      }
      if(this.selectedPaymentOption.name === 'Yappy'){
        payment_type = 'Yappy'
      }

      let data = this.buildPurchasePayload(payment_type)
      if(this.selectedPaymentOption.name === 'Customer credit'){
        if(parseFloat(this.applied_credit) > parseFloat(this.gift_card.total.amount)){
          this.showAlert('Error', 'Charged amount cannot be greater than the total value of the gift card.', 'danger')
          return
        }
        if(parseFloat(this.applied_credit) > parseFloat(this.available_credit)){
          this.showAlert('Error', 'You cannot apply more credits than available.', 'danger')
          return
        }
        data.customer_credit = this.applied_credit
      }
      if(this.selectedPaymentOption.name === 'Gift card'){
        if(!this.gift_card_code
          || !this.selectedGiftCardCode.hasOwnProperty('id')
          || this.getGiftCardCode(this.selectedGiftCardCode.code) !== this.gift_card_code){
          this.errs.gift_card_code = 'You must choose a valid gift card'
          return
        } else {
          this.errs.gift_card_code = ''
        }
        data.applied_gift_card = this.selectedGiftCardCode
        data.payment_type = 'gift_card'
      }

      this.loading = true

      if(this.settings.payment_gateway_id === 1 && this.selectedPaymentOption.name === 'Card'){
        try {
          this.authorize_opaque_data = await this.$refs.authorizePaymentForm.tokenize()
        } catch (err) {
          this.$notify({ type: 'danger', message: err.message })
          this.loading = false
          return
        }
        data.payment = {
          opaqueData: this.authorize_opaque_data,
          zip: this.$refs.authorizePaymentForm.zip
        }
      }
      else if(this.settings.payment_gateway_id === 2 && this.selectedPaymentOption.name === 'Card') {
        const isStoredCard = this.selected_payment_method !== 'new' && this.selectedStoredCard

        if (isStoredCard) {
          // Use stored card
          data.locationId = this.squareLocationId
          data.square_card_id = this.selectedStoredCard.card_id
          data.square_customer_id = this.square_customer?.square_customer_id
          data.payment = this.payment
        } else {
          // New card flow
          const token = await this.tokenize(this.card);
          //SCA
          let verificationToken = await this.verifyBuyer(
            this.square,
            token.token, token.zip
          );
          const token2 = await this.tokenize(this.card);
          data.locationId = this.squareLocationId
          data.sourceId = token.token
          data.sourceId2 = token2.token
          data.verificationToken = verificationToken
          data.zip = token.zip
          data.payment = this.payment
          data.save_card = this.save_card_for_future
        }
      }
      else if(this.settings.payment_gateway_id === 3 && this.selectedPaymentOption.name === 'Card') {
        const isStoredCard = this.selected_payment_method !== 'new' && this.selectedStoredCard

        if (isStoredCard) {
          // Use stored payment method
          data.payment_method_id = this.selectedStoredCard.payment_method_id
          data.stripe_customer_id = this.stripe_customer?.stripe_customer_id
          data.payment = this.payment
        } else {
          // New card flow
          const {token, error} = await this.stripe.createToken(this.card);
          if(error){
            this.loading = false
            return
          }
          data.token = token.id
          data.payment = this.payment
          data.save_card = this.save_card_for_future
        }
      }
      else if (this.settings.payment_gateway_id === 4 && this.selectedPaymentOption.name === 'Card' ){
        await this.payWithPaystack()
        return
      }

      try {
        await this.putTransaction(data)
      } catch (err) {
        this.loading = false
        this.handleCreateError(err)
        return
      }

      this.showAlert('Success', 'Gift card has been created', 'success')
      this.finishCreate()
    },
    // Reset the form and close after a successful create (either path).
    finishCreate(){
      this.gift_card =  {
        type: '',
        custom_code: 0,
        code: '',
        personal_message: '',
        design_url: '',
        custom_amount: '',
        recipient_first_name: '',
        recipient_last_name: '',
        recipient_email: '',
        total: 0,
        quantity: 1,
        send_instantly: 1,
        delivery_date: new Date(),
        unit_price: 0,
        unit_value: 0
      }
      this.payment = {
        cardNumber: '',
        expirationDate: '',
        cardCode: '',
        zip: ''
      }
      this.send_gift_card_to_self = false
      this.no_confirmation_email = false
      this.suppress_delivery_email = false
      this.waive_fee = false
      this.override_price = false
      this.new_price = 0
      this.override_unit_price = null
      this.fee_amounts = {}
      this.promo_discounts = {}
      this.total_discount = 0
      this.selectedPaymentOption = {name: '', content: '', id: '', is_default: ''}
      this.posDevice = null
      this.clearCustomer()
      this.loading = false
      this.$emit('created')
      this.$emit('close')
    },
    async initForm(){
      // Seed settings from the list page so the gateway id is known before the
      // payment-methods call resolves (matches the original open sequence).
      if(this.paymentSettings){
        this.settings = { ...this.paymentSettings }
        if(this.settings.payment_gateway_id === 2){
          this.squareAppId = this.settings.api_name
          this.squareLocationId = this.settings.api_location
        }
      }
      this.promos = []
      // Reset price override fields
      this.override_price = false
      this.new_price = 0
      // Reset stored payment methods
      this.selected_payment_method = 'new'
      this.save_card_for_future = false
      this.resetStripePaymentMethods()
      this.resetSquarePaymentMethods()

      this.selectedGiftCardType = this.giftCardTypes.length > 0 ? this.giftCardTypes[0] : ''
      this.designs = this.selectedGiftCardType ? JSON.parse(this.selectedGiftCardType.designs) : []
      this.posDevice = null
      await this.getPaymentMethods()
      this.gift_card.design_url = this.designs.length > 0 ? this.designs[0] : ''
      this.selectGiftCardType()

      this.$nextTick(() => {
        if(this.settings.payment_gateway_id === 2){
          this.initializeSquare()
        } else if(this.settings.payment_gateway_id === 3){
          this.initializeStripe()
        }
      });

    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getGiftCardCode(code){
      if(!code){
        return
      }
      if(isNaN(code)){
        return code
      }
      let c = code.match(/.{1,4}/g);
      return c.join(' ')
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    getFilteredGames(){
      if(!this.selectedGiftCardType || this.selectedGiftCardType.value_type !== 2) return this.games
      const linked = this.selectedGiftCardType.linked_game_ids
      if(!linked || linked.length === 0) return this.games
      return this.games.filter(g => linked.includes(g.id))
    },  }
}
</script>
